/**
 * Persist Performance date-range picks across visits.
 * Owned keeps 1…365 + Custom. Tracked / Trends / Sentiment clamp 1…90 (#1227).
 *
 * Caption from the `range_days` in the response when it differs from what we
 * sent (asked > 90 on the public tabs). 14 and 60 now match on every tab.
 */
import {
  OWNED_MAX_RANGE_DAYS,
  PUBLIC_MAX_RANGE_DAYS,
} from './posting-insights-data';

const OWNED_KEY = 'ff-admin.marketing.owned-range';
const PUBLIC_KEY = 'ff-admin.marketing.public-range';

export type OwnedRangePreference = {
  range: number;
  customFrom: string | null;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function clampOwnedDays(n: number): number {
  if (!Number.isFinite(n)) return 30;
  return Math.max(1, Math.min(OWNED_MAX_RANGE_DAYS, Math.round(n)));
}

export function loadOwnedRangePreference(): OwnedRangePreference {
  try {
    const raw = localStorage.getItem(OWNED_KEY);
    if (!raw) return { range: 30, customFrom: null };
    const parsed = JSON.parse(raw) as Partial<OwnedRangePreference>;
    const range = clampOwnedDays(Number(parsed.range));
    const customFrom =
      typeof parsed.customFrom === 'string' && ISO_DATE.test(parsed.customFrom)
        ? parsed.customFrom
        : null;
    return { range, customFrom };
  } catch {
    return { range: 30, customFrom: null };
  }
}

export function saveOwnedRangePreference(next: OwnedRangePreference): void {
  try {
    localStorage.setItem(
      OWNED_KEY,
      JSON.stringify({
        range: clampOwnedDays(next.range),
        customFrom:
          next.customFrom && ISO_DATE.test(next.customFrom) ? next.customFrom : null,
      }),
    );
  } catch {
    /* private mode / quota */
  }
}

function clampPublicDays(n: number): number {
  if (!Number.isFinite(n)) return 30;
  return Math.max(1, Math.min(PUBLIC_MAX_RANGE_DAYS, Math.round(n)));
}

export function loadPublicRangePreference(): number {
  try {
    const raw = localStorage.getItem(PUBLIC_KEY);
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 1) return clampPublicDays(n);
  } catch {
    /* ignore */
  }
  return 30;
}

export function savePublicRangePreference(range: number): void {
  try {
    localStorage.setItem(PUBLIC_KEY, String(clampPublicDays(range)));
  } catch {
    /* ignore */
  }
}

/** Opaque UTC day `YYYY-MM-DD`. Do not run through `new Date(date)`. */
export function utcDayKey(date: string | null | undefined): string {
  if (!date) return '';
  return date.length >= 10 ? date.slice(0, 10) : date;
}

/** Axis tick from a UTC day key (`MM-DD`). */
export function utcDayTick(date: string | null | undefined): string {
  const key = utcDayKey(date);
  return key.length >= 10 ? key.slice(5, 10) : key;
}

/** Caption the window the server actually served. Mismatch only when asked > ceiling. */
export function servedWindowLabel(served: number | null | undefined, requested: number): string {
  const n = served ?? requested;
  const base = n === 365 ? '12 mo' : `${n}d`;
  if (served != null && served !== requested) return `${base} served (asked ${requested}d)`;
  return `${base} · UTC`;
}
